import type { Quaternion, Vector3 } from "three";
import type { ConnectionState } from "livekit-client";
import type { DeviceNodeHealth } from "@skydio/channels/src/device_node_health_summary_pb";
import type { GimbalFlashlightStatus } from "@skydio/channels/src/gimbal_flashlight_status_pb";
import type { LandingPadTrackerSimplifiedState } from "@skydio/channels/src/landing_pad_tracker_simplified_state_pb";
import type { ParachuteStatus } from "@skydio/channels/src/parachute_status_pb";
import type { Status as DropperStatus } from "@skydio/channels/src/dropper_status_pb";
import type { ReturnToDockInfo } from "@skydio/channels/src/return_to_dock_info_pb";
import type { SafeLandingInfo } from "@skydio/channels/src/safe_landing_info_pb";
import type { SpeakerMicAudioFiles } from "@skydio/channels/src/speaker_mic_list_audio_files_pb";
import type { SpeakerMicStatus } from "@skydio/channels/src/speaker_mic_status_pb";
import type { SpotlightSystemStatus } from "@skydio/channels/src/spotlight_system_status_pb";
import type { StreamCameraCalibration } from "@skydio/channels/src/stream_camera_calibration_pb";
import type { VehicleSettings } from "@skydio/channels/src/update_vehicle_settings_pb";
import type { MinimalAutoRegionState } from "@skydio/channels/src/user_camera_autoregion_state_pb";
import type { CameraStatus } from "@skydio/channels/src/user_camera_status_ng_pb";
import type { ValueType } from "@skydio/core";
import type { Meters } from "@skydio/math";
import type { FlightState } from "@skydio/pbtypes/pbtypes/gen/ambassador/flight_state_pb";
import type { TransPb } from "@skydio/pbtypes/pbtypes/gen/body/trans_pb";
import type { CameraIntrinsics } from "@skydio/pbtypes/pbtypes/gen/calibration/camera_intrinsics_pb";
import type { VehicleFault } from "@skydio/pbtypes/pbtypes/gen/common/fault_pb";
import type { FlightPhaseEnum } from "@skydio/pbtypes/pbtypes/gen/common/flight_phase_enum_pb";
import type { PlanningPolicyPb } from "@skydio/pbtypes/pbtypes/gen/planner/planning_policy_pb";
import type { GoToWaypointSource } from "@skydio/pbtypes/pbtypes/gen/skills/go_to_waypoint_source_pb";
import type { BlockingPrompt } from "@skydio/pbtypes/pbtypes/gen/user_prompts/blocking_prompt_pb";
import type { PortStatusToConsumers } from "@skydio/pbtypes/pbtypes/infrastructure/drivers/attachment_manager/attachment_manager_pb";
import type { DeviceLinksSummary } from "@skydio/pbtypes/pbtypes/infrastructure/flight_connect/link_summary_pb";
import type { InterceptOrbitPoiMode_Enum } from "@skydio/pbtypes/pbtypes/vehicle/skills/actions/orbit_poi/orbit_poi";
import type { SurfaceScanActionStateData } from "@skydio/pbtypes/pbtypes/vehicle/skills/builtin_skills/surface_scan/surface_scan_pb";
import type { GoToWaypointState } from "@skydio/pbtypes/pbtypes/vehicle/skills/waypoints_pb";
import type { APIRequestState } from "@skydio/redux_util/src";
import type { WebRTCConnectionState } from "@skydio/skybus/src/transport/types";
import type { ActionButtonType } from "components/main/teleop/AutonomyWidget/AutonomyWidgetHeader/ActionButton.types";
import type { StatusIconType } from "components/main/teleop/AutonomyWidget/AutonomyWidgetHeader/StatusIcon.types";
import type { TurtleModeDirection } from "@skydio/pbtypes/pbtypes/gen/airhawk_driver/turtle_mode_direction_pb";
import type { CellularModemStates } from "@skydio/channels/src/cellular_modem_states_pb";
import type { SubjectTrackAndFollowState } from "@skydio/pbtypes/pbtypes/vehicle/subject_follower/subject_follower_pb";
import type { BlockingSource as SubjectFollowBlockingSource } from "@skydio/pbtypes/pbtypes/gen/subject_follower/blocking_source_pb";
import type { ScenarioStatus } from "@skydio/pbtypes/pbtypes/sim/scenario_sim/scenario_status_pb";

export const PositionMovements = ["forwards", "backwards", "left", "right", "up", "down"] as const;
export const HeadingMovements = ["yawLeft", "yawRight"] as const;
export const PitchMovements = ["gimbalUp", "gimbalDown"] as const;
export const AllMovements = [...PositionMovements, ...HeadingMovements, ...PitchMovements];

export type PositionMovementDirection = (typeof PositionMovements)[number];
export type HeadingMovementDirection = (typeof HeadingMovements)[number];
export type PitchMovementDirection = (typeof PitchMovements)[number];

export type MovementDirection =
  | PositionMovementDirection
  | HeadingMovementDirection
  | PitchMovementDirection;

export function isPositionMovement(
  direction: MovementDirection
): direction is PositionMovementDirection {
  return PositionMovements.includes(direction as PositionMovementDirection);
}
export function isHeadingMovement(
  direction: MovementDirection
): direction is HeadingMovementDirection {
  return HeadingMovements.includes(direction as HeadingMovementDirection);
}
export function isPitchMovement(direction: MovementDirection): direction is PitchMovementDirection {
  return PitchMovements.includes(direction as PitchMovementDirection);
}

export type TeleopAction =
  | "toggleObstacleAvoidance"
  | "togglePointerLock"
  | "increaseSpeed"
  | "decreaseSpeed"
  | "boost"
  | "crawl"
  | "zoomIn"
  | "zoomOut"
  | "increaseExposure"
  | "decreaseExposure"
  | "resetExposure"
  | "takePhoto"
  | "workflowNext"
  | "workflowPrevious"
  | "launchOrReturnToDock"
  | "cancel"
  | "toggleControlsMenu"
  | "autoFocusCenter"
  | "clearFocusCenter"
  | "zoomOneShortcut"
  | "zoomTwoShortcut"
  | "zoomThreeShortcut"
  | "zoomFourShortcut"
  | "zoomFiveShortcut"
  | "zoomSixShortcut"
  | "toggleStrobeLights"
  | "thermalToggle"
  | "resetGimbalPitch"
  | "toggleRgbLights"
  | "toggleARMarkers"
  | "createNewMarker"
  | "selectSubject"
  | "toggleCrosshairInfo"
  | "toggleCurrentMission"
  | "toggleARStreetOverlays"
  | "toggleSubjectTracking"
  | "toggleSubjectFollowing"
  | "__DEMO_ONLY_triggerDedroneAlert" /* This is a demo-only action that triggers a dedrone alert */
  | "deployParachuteFirstKey"
  | "deployParachuteSecondKey"
  | "deployDropper";

export interface MouseOrientation {
  pitch: number;
  heading: number;
}

export enum GamepadInputType {
  AXIS = "AXIS",
  BUTTON = "BUTTON",
}
// map common name to axis/button we read in value from
export enum GamepadMovementInputs {
  GAMEPAD_AXES_LEFT_STICK_X = 0,
  GAMEPAD_AXES_LEFT_STICK_Y = 1,
  GAMEPAD_AXES_RIGHT_STICK_X = 2,
  GAMEPAD_AXES_RIGHT_STICK_Y = 3,
  GAMEPAD_BUTTON_LT = 6,
  GAMEPAD_BUTTON_RT = 7,
}
export type GamepadMovementInputInfo = { input: GamepadMovementInputs; type: GamepadInputType };

// gamepad support for teleop actions
export enum GamepadActionInputs {
  GAMEPAD_BUTTON_DPAD_UP = 12,
  GAMEPAD_BUTTON_DPAD_DOWN = 13,
  GAMEPAD_BUTTON_DPAD_LEFT = 14,
  GAMEPAD_BUTTON_DPAD_RIGHT = 15,
  GAMEPAD_BUTTON_Y = 3,
  GAMEPAD_BUTTON_X = 2,
  GAMEPAD_BUTTON_RB = 5,
  GAMEPAD_BUTTON_LB = 4,
  GAMEPAD_BUTTON_A = 0,
  GAMEPAD_BUTTON_B = 1,
}

// NOTE(sam): Eventually we should implement some more sophisticated logic to determine what skills
// we show, but for now we just want to show these to all users
// See SkillSelector.tsx for some logic on which to show. These values should match mobile params.
export enum CLOUD_SKILLS {
  WAYPOINTS = "MissionRunner",
  LEGACY_MISSION_PLANNER = "PatrolPlanner",
  MISSION_PLANNER = "MissionPlanner",
  MANUAL = "FreeFly",
  SITE_MAPPER = "SiteMapper",
  // special case, this is not a vehicle skill
  MISSION_PLANNER_V2 = "MissionPlannerV2",
  LONG_RANGE_TRIPOD = "LongRangeTripod",
}

export type speedSetting = {
  min: number;
  max: number;
  default: number;
  step: number;
};

export enum MANUAL_FLIGHT_BLOCKERS {
  SETTINGS_MENU_OPEN = "SETTINGS_MENU_OPEN",
  FEEDBACK_MENU_OPEN = "FEEDBACK_MENU_OPEN",
  DEBUG_MENU_OPEN = "DEBUG_MENU_OPEN",
  CLICK_TO_FLY = "CLICK_TO_FLY",
  GO_TO_WAYPOINT = "GO_TO_WAYPOINT",
  INPUT_FOCUSED = "INPUT_FOCUSED",
  MARKER_EDIT_MODAL_OPEN = "MARKER_EDIT_MODAL_OPEN",
  MARKER_DELETE_MODAL_OPEN = "MARKER_DELETE_MODAL_OPEN",
}

export type DroneSenseMission = {
  missionId: string;
  name: string;
};

type DroneSenseEmptyState = {
  requestStatus: "UNINITIALIZED" | "REQUESTED" | "ERROR";
};

type DroneSenseSuccessState = {
  requestStatus: "SUCCESS";
  missions: DroneSenseMission[];
  selectedMissionId?: string;
  callSign: string;
};

export type DroneSense = DroneSenseEmptyState | DroneSenseSuccessState;

export type AutonomyWidgetStatus = {
  show?: boolean;
  statusTitle?: string;
  statusText?: string;
  progressRatio?: number;
  customStopIconHash?: string;
  customStopIconId?: string;
  allowsLanding?: boolean;
  isMissionPaused?: boolean;
  isMissionActive?: boolean;
  waypointIndex?: number;
  numWaypoints?: number;
  remainingTimeEstimate?: string;
  remainingDistanceEstimate?: number;
  mapCapture?: SurfaceScanActionStateData.AsObject | null;
  missionName?: string;
  showDropdown?: boolean;
  statusType?: StatusIconType;
  actionButtonType?: ActionButtonType;
  actionButtonTooltipText?: string;
  isCompact?: boolean;
  isFlyToPointMission?: boolean;
  isUsingPathfinder?: boolean;
};

export interface PilotState {
  vehicleConnection: APIRequestState & {
    clientId: string;
    vehicleId: string;
    downloadBitrate: number;
    uploadBitrate: number;
    status: WebRTCConnectionState | ConnectionState;
  };
  sessionId: string;
  runmode: string;
  activeFaults: number[];
  /** True after we receive SYSTEM_HEALTH_MONITOR_ACTIVE_FAULT_IDS (LCM); PB fault updates are ignored once set. */
  hasReceivedActiveFaultsLcm: boolean;
  takeoffRequested: boolean;
  commandeerRequested: boolean;
  flightState?: ValueType<FlightState.EnumMap>;
  obstacleSetting: {
    safety?: number;
    mode?: ValueType<VehicleSettings.ObstacleMarginModeMap>;
  };
  movementKeybindings: Record<MovementDirection, string>;
  teleopActionKeybindings: Record<TeleopAction, string>;
  keyboardMovementInputs: Record<MovementDirection, number>; // number corresponds to first key press time
  teleopActionInputs: Record<TeleopAction, number>; // number corresponds to first key press time
  teleopActionSources: Record<TeleopAction, string>; // source of teleop action: gamepad or keyboard
  teleopSubjectTrackingControlMode: "mouse" | "gamepad"; //  determines whether mouse or gamepad was used last. We don't care about keyboard.
  mouseInput: {
    orientation: Partial<MouseOrientation>; // allow nullable fields
    locked: boolean;
    lastMouseMove: number; // timestamp of last mouse move event
  };
  gamepadConnected: boolean;
  gamepadMapping: GamepadMappingType;
  gamepadMovementInputs: Record<MovementDirection, number>; // number corresponds to analog value
  clickToFly: {
    clickStart: Vector3;
    clickEnd: Vector3;
    scrollEnd: Vector3;
    active: number;
    displayTrail: boolean;
  };
  skillState?: string; // TODO - get useful information from skill
  blockingPrompt?: BlockingPrompt.AsObject;
  activePromptsList: BlockingPrompt.AsObject[];
  buttonInputs: {
    elevation?: number;
    range?: number;
  };
  flightId: string;
  flightPhaseId: ValueType<FlightPhaseEnum.EnumMap>;
  movingAutonomously?: boolean;
  isAutonomyOff: boolean;
  mostRecentPilot: {
    clientId: string;
    updatedAt: number;
    pilotCloudUserId?: string;
  };
  policyCommandType?: ValueType<PlanningPolicyPb.EnumMap>;
  pitchControlMode: "position" | "velocity";
  headingControlMode: "position" | "velocity";
  positionControlMode: "position" | "velocity";
  desiredPose: { position?: Vector3; heading?: number; pitch?: number; timestamp?: number };
  imu_T_subject_cam?: TransPb.AsObject;
  eo_imu_T_cam?: TransPb.AsObject;
  ir_imu_T_cam?: TransPb.AsObject;
  gimbal_cam_R_imu?: Quaternion;
  nav_T_gimbal_camera_imu?: { transform: TransPb.AsObject; timestamp: number };
  cameraIntrinsics?: CameraIntrinsics.AsObject;
  vehiclePosition?: Vector3;
  vehicleVelocity?: Vector3;
  vehicleOrientation?: Quaternion;
  vehicleNominalTranslationSpeed: number;
  desiredVehicleNominalTranslationSpeed: number;
  desiredZoomLevel: number;
  zoomCorrectionFactor: number;
  vehicleSpeedChangeTime: number;
  vehicleSpeedSettings: speedSetting;
  crawlModeActive: boolean;
  boostModeActive: boolean;
  ignoreObstacleBasedSpeedSetting: boolean;
  displayPointerLockInnerRing: boolean;
  goToWaypointStatus: {
    state?: ValueType<GoToWaypointState.EnumMap>;
    source?: ValueType<GoToWaypointSource.EnumMap>;
  };
  returnToDockInfo?: ReturnToDockInfo.AsObject;
  safeLandingInfo?: SafeLandingInfo.AsObject;
  latestValidTargetLandingPointId?: SafeLandingInfo.AsObject["targetId"];
  gimbalFlashlightStatus?: GimbalFlashlightStatus.AsObject;
  thirdPersonViewActive: boolean;
  manualFlightBlockers: {
    blocked: boolean;
    blockers: MANUAL_FLIGHT_BLOCKERS[];
    blockerRemovalTimestamp: number;
  };
  streamCameraCalibration?: StreamCameraCalibration.AsObject;
  configReceived: boolean;
  landingPadTracker?: LandingPadTrackerSimplifiedState.AsObject;
  rssi?: number;
  vehicleAmbassadorStatusLastUpdated?: Date;
  /**
   * The default height to fly to when using the "Fly Here Now" feature.
   * Used for both POIs and fly-by waypoints.
   */
  defaultPoiHeight: Meters;
  /**
   * Information about the user camera. Useful for determining state of various components like
   * "Is the vehicle currently recording?", "What is the focus region set to?", etc
   */
  userCameraAutoRegionState?: MinimalAutoRegionState.AsObject;
  userCameraStatus?: CameraStatus;
  /**
   * The default orbit intercept mode to use when using the "Orbit Point" feature.
   */
  defaultOrbitInterceptMode: InterceptOrbitPoiMode_Enum;
  /**
   * The default orbit direction to use when using the "Orbit Point" feature.
   */
  defaultOrbitDirection: "clockwise" | "counterclockwise";
  /**
   * The default mode for flying between points
   */
  defaultPathBetweenPointsMode: "upAndOver" | "pathfinder";
  /**
   * Control whether the look_at_point option is enabled for the "Orbit Point" feature.
   */
  defaultLookAtPointActive: boolean;
  /**
   * If active, the vehicle will look at the final point of the mission in a "Fly Here" mission
   */
  lookAtFinalPointActive: boolean;
  /**
   * If true, the vehicle will ramp up and down at an angle instead of going straight up / down to
   * the traversal height in a "Fly Here" mission
   */
  rampHeightModeActive: boolean;
  /**
   * The value in meters at which the fly to point will automatically standoff itself
   */
  flyToPointStandoffDistance: number;
  // attachment status as reported by th ATTACHMENT_PORT_STATUS_AM_PB channel
  attachmentPortStatus?: PortStatusToConsumers.AsObject;
  // system status of the spotlight attachment
  spotlightSystemStatus?: SpotlightSystemStatus;
  // link summary of a device node
  deviceLinksSummary: { [key: string]: DeviceLinksSummary };
  // health summary of a device node
  deviceNodeHealth: { [key: string]: DeviceNodeHealth };
  /**
   * In night autonomy navigation mode with nightsense modules activated
   */
  nightSenseActive: boolean;
  speakerMicStatus?: SpeakerMicStatus;
  speakerMicAudioFiles?: SpeakerMicAudioFiles;
  /**
   * State of the DroneSense mission integration for this pilot
   */
  droneSense: DroneSense;
  lastKnownFlyingFlightId?: string;
  /**
   * State to check if the vehicle is far away from the launch point in dockless sites
   * and track pilot dismissal of the modal
   */
  farAwayFromLaunchPoint?: boolean;
  farAwayFromLaunchPointDismissed?: boolean;
  // status of the parachute
  parachuteStatus?: ParachuteStatus;
  // status of the dropper
  dropperStatus?: DropperStatus;
  // list of blocking faults, from pb channel
  blockingFaultsPb?: Array<VehicleFault.EnumMap[keyof VehicleFault.EnumMap]>;
  // subject tracking index
  subjectTrackingIndex?: number;
  // subject track / follow state
  subjectState?: ValueType<SubjectTrackAndFollowState.EnumMap>;
  // subject follow blocking source
  subjectFollowBlockedReason?: ValueType<SubjectFollowBlockingSource.EnumMap>;
  // autonomy widget status
  autonomyWidgetStatus?: AutonomyWidgetStatus;
  /** Latest Paraverse / scenario_sim status from PARAVERSE_SCENARIO_STATUS_PB (teleop). */
  paraverseScenarioStatus?: ScenarioStatus.AsObject;
  // turtle mode recommendation
  turtleModeRecommendation?: ValueType<TurtleModeDirection.EnumMap>;
  // vehicle-computed crosshair position from on-board DTM raycast
  vehicleCrosshairPosition?: {
    lat: number;
    lng: number;
    altMsl: number | null;
    status: number;
  };
  // cellular modem states
  cellularModemStates?: CellularModemStates;
  // a22 sim options
  a22SimOptions?: { label: string; value: number }[];
  // vehicle sim options (for vehicles with built-in dual SIM, e.g. R71)
  vehicleSimOptions?: { label: string; value: number }[];
  autonomousFlightSpeed: { policySetting: number; userSetting: number | null };
  // remote id broadcasting status
  remoteIdBroadcasting?: boolean;
}
